<template>
  <v-app>
    <v-container fluid>
        <v-form @submit.prevent="submit">
            <v-row>
                <v-col cols="12">
                <v-expansion-panels v-model="panel">
                    <v-expansion-panel>
                    <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                        Basic Info
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        
                        <v-row class="pa-2">
                            <v-col lg="3" md="4" cols="12">
                                <v-autocomplete
                                    v-model="year"
                                    :items="yearList"
                                    item-title="label"
                                    item-value="value"
                                    label="Enter Year"
                                    variant="outlined"
                                    density="compact"
                                    bg-color="grey-lighten-4"
                                    prepend-inner-icon="mdi-calendar-month"
                                    clearable
                                />
                            </v-col>
                            <!-- Employee Selector -->
                            <v-col lg="3" md="4" cols="12">
                                <v-autocomplete
                                    v-model="employee"
                                    :items="hrmEmployeeList"
                                    :item-title="item => `${item.code}-${item.name}` ?? 'N/A'"
                                    item-value="id"
                                    label="Select Employee"
                                    variant="outlined"
                                    density="compact"
                                    bg-color="grey-lighten-4"
                                    clearable
                                    :loading="loading"
                                    prepend-inner-icon="mdi-account"
                                    @update:modelValue="generateLeaveTypeList"
                                />
                            </v-col>
                        </v-row>
                    </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
                </v-col>
            </v-row>

            <!-- 🔹 Employee Leave Table -->
            <v-row>
                <v-col cols="12">
                    <v-expansion-panels v-model="panel1">
                        <v-expansion-panel>
                            <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                                Details
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                            <v-row>
                                <v-col cols="12" class="mt-3" style="display:flex; justify-content:center;">
                                    <div>
                                        <v-table class="bordered-table" style="width: 600px; margin-bottom: 15px;">
                                            <tbody>
                                                <tr v-for="leaveType in leaveTypeList">
                                                    <td style="max-width: 50px; font-size:16px; text-align: right;"><b>{{ leaveType.name }}</b></td>
                                                    <td>
                                                        <v-text-field
                                                            v-model="leaveType.max_days"
                                                            label="Total Days*"
                                                            min="0"
                                                            :type="number"
                                                            dense
                                                            hide-details
                                                            variant="outlined"
                                                            density="compact"
                                                        />
                                                    </td>
                                                    <td class="d-none">
                                                        <v-text-field
                                                            v-model="leaveType.used_days"
                                                            :type="number"
                                                            min="0"
                                                            label="Used Days"
                                                            dense
                                                            hide-details
                                                            variant="outlined"
                                                            density="compact"
                                                        />
                                                    </td>
                                                    <td class="d-none">
                                                        <v-text-field
                                                            v-model="leaveType.carried_forward"
                                                            label="Carried Forward"
                                                            min="0"
                                                            :type="number"
                                                            dense
                                                            hide-details
                                                            variant="outlined"
                                                            density="compact"
                                                        />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </v-table>
                                        <v-card-actions v-if="leaveTypeList && leaveTypeList.length"> 
                                            <v-col cols="12" align="right">
                                                <v-btn color="primary" variant="flat" type="submit" class="ms-2"> Submit </v-btn>
                                            </v-col>
                                        </v-card-actions>
                                    </div>
                                </v-col>
                            </v-row>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
  </v-app>
</template>

<script setup>
    import { ref, reactive, computed, nextTick, onMounted } from "vue";
    import { useStore } from "vuex";
    import { toast } from "vue3-toastify";

    const store = useStore();

    const panel = ref([0]);
    const panel1 = ref([0]);
    const employee = ref(null);
    const year = ref(new Date().getFullYear());
    const loading = ref(false);
    const leaveTypeList = ref([]);

    const hrmEmployeeList = computed(() => store.getters.getHrmEmployeeList || []);
    const selectedEmployeeLeaveBalance = computed(() => store.getters.selectedEmployeeLeaveBalance || []);

    const yearList = computed(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 2 }, (_, i) => currentYear - 1 + i);
    });

    const fetchHrmEmployees = async () => {
        loading.value = true;
        await store.dispatch("getHrmEmployeeList",{is_all_branch:true}).finally(() => (loading.value = false));
    };

    const  generateLeaveTypeList = async () => {
        if(!employee.value){
            store.commit("UNSET_LEAVE_TYPE_LIST");
            store.commit("UNSET_LEAVE_BALANCE_LIST");
            return;
        }

        loading.value = true;
        const leaveTypes = await store.dispatch("getLeaveTypeList", {employee_id: employee.value}).finally(() => loading.value = false);
        loading.value = true;
        const leaveBalances = await store.dispatch("getLeaveBalanceList", {employee_id: employee.value}).finally(() => loading.value = false);

        leaveTypeList.value = leaveTypes.data.data.map(type => { 
            const balance = (leaveBalances.data.data?.[0])?.leave_balances.find(b => b.leave_type_id === type.id);
            return {
                ...type,
                max_days: balance ? balance.remaining_days : type.max_days,
                used_days: balance ? balance.used_days : 0,
                carried_forward: balance ? balance.carried_forward : 0,
            };
        });
    };

    const submit = async () => {
        if (!employee.value || !year.value) {
            toast.error("Employee and Year are required");
            return;
        }

        const payload = {
            employee_id: employee.value,
            year: year.value,
            leave_type_id: [],
            entitled_days: [],
            used_days: [],
            carried_forward: [],
        };

        leaveTypeList.value.forEach(item => {
            payload.leave_type_id.push(item.id);
            payload.entitled_days.push(Number(item.max_days) || 0);
            payload.used_days.push(Number(item.used_days) || 0);
            payload.carried_forward.push(Number(item.carried_forward) || 0);
        });

        loading.value = true;
        try {
            await store.dispatch("employeeLeaveProcess", payload);
            toast.success("Leave processed successfully");
        } catch (err) {
            toast.error(err.response?.data?.message || "Something went wrong");
            loading.value = false;
        } finally {
            loading.value = false;
        }
    };

    onMounted(async () => {
        if (selectedEmployeeLeaveBalance.value) {
            let leaveBalance = selectedEmployeeLeaveBalance.value;
            year.value = leaveBalance.year;
            employee.value = leaveBalance.employee_id;
            generateLeaveTypeList();
        }
        store.commit("UNSET_SELECTED_EMPLOYEE_LEAVE_BALANCE");
        store.commit("UNSET_LEAVE_TYPE_LIST");
        await fetchHrmEmployees();
    });
</script>


<style scoped>
    .panel-header {
    font-weight: 600;
    }
    table th,
    table td {
    padding: 4px 8px;
    }
    @media (max-width: 600px) {
    table {
        font-size: 12px;
    }
    }
    .bordered-table {
    border: 1px solid #dcdcdc;
    border-collapse: collapse;
    }

    .bordered-table th,
    .bordered-table td {
    border: 1px solid #dcdcdc;
    padding: 8px;
    }
</style>

